import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Handle, Position, useReactFlow, useNodes } from 'reactflow';
import { ExclamationTriangleIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { BsBank } from "react-icons/bs";
import { GrConfigure } from 'react-icons/gr';
import './LoanNode.css'; // Import the CSS file
import LoanConfigModal from './LoanConfigModal';
import { usePipeline } from '../../../context/PipelineContext';
import { NodeMappingContext } from '../../Canvas/Canvas';
import { notifyError } from '../../../errors/notifyError';

const LoanNode = ({ id, data, selected }) => {
  const { loanPerc, annualRate, tenureYears, moratoriumYears, name } = data;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const reactFlowInstance = useReactFlow();
  const nodes = useNodes();
  const { session, addLoanNode, updateSelectedNode } = usePipeline();
  const { updateNodeMapping } = useContext(NodeMappingContext);
  
  // Log the data received by the component for debugging
  useEffect(() => {
    console.log(`LoanNode ${id} received data:`, data);
    // Log specific loan parameters for debugging
    console.log(`LoanNode ${id} loan parameters:`, {
      loanPerc,
      annualRate,
      tenureYears,
      moratoriumYears,
      name
    });
  }, [id, data, loanPerc, annualRate, tenureYears, moratoriumYears, name]);

  // Check if node is configured properly
  // Either explicitly marked as configured or has all required parameters AND a name
  const isConfigured = data.isConfigured || (!!name && !!loanPerc && !!annualRate && !!tenureYears && moratoriumYears !== undefined);
  
  // Log node state for debugging
  useEffect(() => {
    console.log(`LoanNode ${id} state:`, {
      isConfigured,
      name,
      loanPerc,
      annualRate,
      tenureYears,
      moratoriumYears,
      dataIsConfigured: data.isConfigured,
      dataIsApiConfigured: data.isApiConfigured
    });
  }, [id, isConfigured, name, loanPerc, annualRate, tenureYears, moratoriumYears, data]);

  // Get the current node position
  const getNodePosition = useCallback(() => {
    const node = nodes.find(n => n.id === id);
    return node ? node.position : { x: 0, y: 0 };
  }, [nodes, id]);

  const openConfigModal = useCallback(() => {
    // Log the current state for debugging
    console.log("openConfigModal called with state:", {
      isConfigured,
      loanPerc,
      annualRate,
      tenureYears,
      moratoriumYears,
      isModalOpen
    });
    
    // Open the configuration modal
    console.log(`Opening modal for ${isConfigured ? 'editing' : 'configuration'}`);
    
    // Force a small delay to ensure React state updates properly
    setTimeout(() => {
      setIsModalOpen(true);
      console.log("Modal should be open now:", { isModalOpen: true });
    }, 50);
  }, [isConfigured, loanPerc, annualRate, tenureYears, moratoriumYears, isModalOpen]);

  const closeConfigModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleConfigured = useCallback(async (configData) => {
    // Start the configuration process
    setIsConfiguring(true);
    
    try {
      const { name, loanPerc, annualRate, tenureYears, moratoriumYears } = configData;
      const position = getNodePosition();
      
      // Log the configuration data for debugging
      console.log(`Configuring loan node ${id} -> ${name}:`, {
        configData,
        position,
        isConfigured: data.isConfigured,
        isApiConfigured: data.isApiConfigured
      });
      
      // First update the node ID to name mapping before adding to session
      updateNodeMapping(id, name);
      console.log(`Updated node mapping: ${id} -> ${name}`);
      
      // Add the loan node to the session state
      addLoanNode(
        name,
        loanPerc,
        annualRate,
        tenureYears,
        moratoriumYears,
        { x: position.x, y: position.y }
      );
      
      // Update the node data with the new configuration
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                name,
                loanPerc,
                annualRate,
                tenureYears,
                moratoriumYears,
                isConfigured: true,
                isApiConfigured: true
              }
            };
          }
          return node;
        })
      );
      
      // If the node was previously selected, update the selection with the new name
      if (session.selected_node === id) {
        console.log(`Node was selected, updating selection from ${id} to ${name}`);
        updateSelectedNode(name);
      }
    } catch (error) {
      console.error('Error configuring loan node:', error);
      notifyError(error);
    } finally {
      setIsConfiguring(false);
      closeConfigModal();
    }
  }, [id, reactFlowInstance, getNodePosition, closeConfigModal, updateNodeMapping, addLoanNode, session, updateSelectedNode, data]);

  return (
    <>
      <div
        className={`loan-node ${selected ? 'loan-selected' : isConfigured ? 'loan-configured' : 'loan-not-configured'}`}
      >
        <div className={`loan-node-header ${isConfigured ? 'loan-configured' : 'loan-not-configured'}`}>
          <div className="loan-node-header-title">
            <div className="loan-node-type">Loan</div>
            {name && isConfigured && <div className="loan-node-name-display">{name}</div>}
          </div>
          <div className="loan-node-header-icons">
            {!isConfigured && (
              <button 
                onClick={openConfigModal}
                className="loan-config-button"
                title="Configure Loan Node"
                disabled={isConfiguring}
              >
                <ExclamationTriangleIcon className="loan-icon" />
              </button>
            )}
            {isConfigured && (
              <button 
                onClick={openConfigModal}
                className="loan-config-button"
                title="Edit Loan Parameters"
                disabled={isConfiguring}
              >
                <Pencil1Icon className="loan-icon" />
              </button>
            )}
            <BsBank className="loan-icon" />
          </div>
        </div>
        <div className="loan-node-content">
          <div className="loan-text-xs">
            {!isConfigured && (
              <div className="loan-text-warning">
                Parameters not configured
              </div>
            )}
            {isConfiguring && (
              <div className="loan-text-info">
                Configuring...
              </div>
            )}
            {isConfigured && (
              <>
                <div>Loan %: {loanPerc}</div>
                <div>Rate: {annualRate}%</div>
                <div>Tenure: {tenureYears} years</div>
                <div>Moratorium: {moratoriumYears} years</div>
              </>
            )}
          </div>
          <div className="loan-flex-container">
            {/* Only output handle, no input handle */}
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              className={`loan-connection-point ${isConfigured ? 'loan-configured-output' : 'loan-not-configured'}`}
              isConnectable={isConfigured} // Only allow connections if the node is configured
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
            />
          </div>
        </div>
      </div>

      <LoanConfigModal
        isOpen={isModalOpen}
        onClose={closeConfigModal}
        onConfigured={handleConfigured}
        initialData={{ 
          name, 
          loanPerc,
          annualRate,
          tenureYears,
          moratoriumYears,
          isConfigured: data.isConfigured,
          position: getNodePosition()
        }}
      />
    </>
  );
};

export default LoanNode;